import { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { districts, DistrictsResponse } from '../api/districts';
import { useAlert } from '../contexts/AlertContext';

export const useLeaderboardDistricts = (
  eventId?: number,
  districtName?: string,
  dateFrom?: Date | null,
  dateTo?: Date | null
) => {
  const [data, setData] = useState<DistrictsResponse | null>(null);
  const [pageSize] = useState(10);
  const [pageNumber, setPageNumber] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const { showAlert } = useAlert();

  const offset = useMemo(() => (pageNumber - 1) * pageSize, [pageNumber, pageSize]);

  const fetchData = async () => {
    if (!eventId) return;

    try {
      setIsLoading(true);
      const response = await districts.getByEventId(eventId, {
        limit: pageSize,
        offset,
        districtName: districtName?.trim() || undefined,
        dateFrom: dateFrom
          ? format(
              new Date(dateFrom.getFullYear(), dateFrom.getMonth(), dateFrom.getDate(), 0, 0, 0),
              "yyyy-MM-dd'T'HH:mm:ss"
            )
          : undefined,
        dateTo: dateTo
          ? format(
              new Date(dateTo.getFullYear(), dateTo.getMonth(), dateTo.getDate(), 23, 59, 59),
              "yyyy-MM-dd'T'HH:mm:ss"
            )
          : undefined,
      });
      setData(response);
    } catch (error) {
      showAlert('Failed to fetch areas', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, pageSize, offset, districtName, dateFrom?.getTime(), dateTo?.getTime()]);

  const totalPages = useMemo(() => {
    return data?.pageSize ? Math.ceil((data?.totalCount || 0) / data.pageSize) : 0;
  }, [data]);

  return {
    districts: data?.districts || [],
    totalPages,
    pageNumber,
    setPageNumber,
    refetch: fetchData,
    isLoading,
  };
};
